import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { usePortalMutation } from 'src/Libs/api'
import { queryFnWithApiResult } from 'src/Libs/api/utils'
import { updateEmcConfig, updateInstallationConfig, updateStorageConfig } from 'src/Libs/flow/installation/api'
import { getDeviceLinks } from 'src/Libs/installations/api/device-link.api'
import { EM360_DEFAULT_CHARGING_STRATEGY, EM360_DEFAULT_LIMIT_POWER_SETTINGS, EM360_DEFAULT_MODBUS_SETTINGS } from 'src/Libs/installations/constants'

interface DeviceConfigVariables {
  installationId: number
  emcDeviceLinkId: string
}

interface UseSavePlannedInstallationEm360DefaultsResult {
  saveDefaults: (installationId: number) => Promise<void>
}

export const useSavePlannedInstallationEm360Defaults = (): UseSavePlannedInstallationEm360DefaultsResult => {
  const authFetch = useAuthFetchWithResponse()

  const { mutateAsync: saveChargingStrategy } = usePortalMutation({
    mutationFn: (installationId: number) => updateInstallationConfig(installationId, EM360_DEFAULT_CHARGING_STRATEGY, authFetch),
  })

  const { mutateAsync: saveModbusSettings } = usePortalMutation({
    mutationFn: ({ installationId, emcDeviceLinkId }: DeviceConfigVariables) =>
      updateEmcConfig(installationId, emcDeviceLinkId, EM360_DEFAULT_MODBUS_SETTINGS, authFetch),
  })

  const { mutateAsync: saveLimitPowerSettings } = usePortalMutation({
    mutationFn: ({ installationId, emcDeviceLinkId }: DeviceConfigVariables) =>
      updateStorageConfig(installationId, emcDeviceLinkId, EM360_DEFAULT_LIMIT_POWER_SETTINGS, authFetch),
  })

  const saveDefaults = async (installationId: number): Promise<void> => {
    const deviceLinks = await queryFnWithApiResult(getDeviceLinks)(authFetch, installationId.toString())
    const emcDeviceLink = deviceLinks?.find(deviceLink => deviceLink.deviceType === 'emc')
    if (!emcDeviceLink) {
      throw new Error(`EMC device link not found for planned installation ${installationId}`)
    }

    const variables = { installationId, emcDeviceLinkId: emcDeviceLink.id.toString() }
    await Promise.all([saveChargingStrategy(installationId), saveModbusSettings(variables), saveLimitPowerSettings(variables)])
  }

  return { saveDefaults }
}
